import {apiGet, apiPost, apiPut, apiUpload} from './client'
import {formatDate} from './gramps'
import {jobDate} from './jobs'

/** Ответ POST /api/media/ — это транзакция: список изменений,
 *  в первом лежит только что созданный медиа-объект. */
interface TxOut {
  handle?: string
  _class?: string
}

/** Загружает изображение и возвращает handle нового медиа-объекта.
 *  Gramps сам считает контрольную сумму и кладёт файл под неё, поэтому
 *  повторная загрузка того же снимка не плодит дубликаты на диске. */
export async function uploadPhoto(blob: Blob): Promise<string> {
  const res = await apiUpload<TxOut[]>('/media/', blob)
  // в транзакции может быть не один элемент — берём именно медиа-объект,
  // а не первый попавшийся
  const h = (res || []).find(i => i._class === 'Media')?.handle || res?.[0]?.handle
  if (!h) throw new Error('Сервер не вернул идентификатор загруженного файла')
  return h
}

export const mediaRef = (ref: string) => ({
  _class: 'MediaRef',
  ref,
  rect: null,
  private: false,
  citation_list: [],
  note_list: [],
  attribute_list: [],
})

/** Максимум для снимка. Сканы бывают тяжёлые, но на 20 МБ уже стоит
 *  спросить, точно ли это то фото. */
export const MAX_PHOTO = 20 * 1024 * 1024

/* Видео заведомо тяжелее. Сервер ничего не ограничивает
 * (MAX_CONTENT_LENGTH не задан), наш nginx принимает тело любого размера
 * и не буферизует его, потолок в NPM — 2 ГБ. 300 МБ здесь не техническое
 * ограничение, а здравый смысл: семейный ролик столько не весит, а вот
 * случайно выбранный фильм — весит. */
export const MAX_VIDEO = 300 * 1024 * 1024

export const isImage = (f: {type?: string}) => !!f.type && f.type.startsWith('image/')

/** Ролик. Сервер принимает любой тип и отдаёт файл с поддержкой Range,
 *  так что перемотка работает без единой правки на бэкенде. */
export const isVideo = (f: {type?: string}) => !!f.type && f.type.startsWith('video/')

/** Что галерея вообще берёт. */
export const isMedia = (f: {type?: string}) => isImage(f) || isVideo(f)

export const sizeLimit = (f: {type?: string}) => (isVideo(f) ? MAX_VIDEO : MAX_PHOTO)

/** По имени файла: у объекта из базы mime лежит в поле mime. */
export const looksVideo = (mime?: string) => !!mime && mime.startsWith('video/')

/* ---------- дата снимка и описание ----------
 *
 * И то, и другое — собственные поля медиа-объекта в Gramps: date такой же,
 * как у события (годится «1979», «03.1979» и полная дата), а desc — его
 * описание. Своих полей заводить не пришлось, и написанное видно в родном
 * интерфейсе тоже.
 *
 * Раньше в desc лежало имя файла — им подписывались плитки, когда больше
 * подписывать было нечем. Теперь это место описания, а имя файла остаётся
 * именем файла.
 */

export interface MediaMeta {
  /** дата как её вводят: дд.мм.гггг, мм.гггг или гггг */
  date: string
  desc: string
}

interface RawMedia {
  handle: string
  date?: {dateval?: number[]; modifier?: number}
  desc?: string
  [k: string]: unknown
}

export async function loadMediaMeta(handle: string): Promise<MediaMeta> {
  const m = await apiGet<RawMedia>(`/media/${handle}`)
  return {date: formatDate(m.date?.dateval), desc: m.desc || ''}
}

export async function saveMediaMeta(handle: string, meta: MediaMeta): Promise<void> {
  const full = await apiGet<RawMedia>(`/media/${handle}`)
  await apiPut(`/media/${handle}`, {
    ...full,
    date: jobDate(meta.date, ''),
    desc: meta.desc.trim(),
  })
}

/** Год снимка числом — для подсчёта возраста. 0, если даты нет. */
export const mediaYear = (m: {date?: {dateval?: number[]}}): number =>
  m.date?.dateval?.[2] || 0

/** Дата снимка так, как её ввели: «14.05.1979», «05.1979» или «1979». */
export const mediaDate = (m: {date?: {dateval?: number[]}}): string =>
  formatDate(m.date?.dateval)

/* ---------- привязка снимка из архива к человеку ---------- */

/**
 * Прицепить уже загруженный снимок к человеку.
 *
 * Нужно для групповых фотографий и для того, что импорт оставил
 * неприкаянным: загрузить файл заново было бы вторым объектом на тот же
 * кадр, а связать существующий раньше было нечем.
 *
 * Читаем персону целиком и пишем целиком: PUT собирает объект заново,
 * и всё, чего нет в теле, стирается.
 */
export async function attachMedia(person: string, media: string): Promise<void> {
  const p = await apiGet<Record<string, unknown>>(`/people/${person}`)
  const list = (p.media_list as {ref: string}[] | undefined) || []
  if (list.some(r => r.ref === media)) return
  await apiPut(`/people/${person}`, {...p, media_list: [...list, mediaRef(media)]})
}

/** Отцепить снимок от человека. Сам объект и файл остаются в архиве. */
export async function detachMedia(person: string, media: string): Promise<void> {
  const p = await apiGet<Record<string, unknown>>(`/people/${person}`)
  const list = (p.media_list as {ref: string}[] | undefined) || []
  if (!list.some(r => r.ref === media)) return
  await apiPut(`/people/${person}`, {...p, media_list: list.filter(r => r.ref !== media)})
}

/* ---------- метка «групповой снимок» ---------- */

/**
 * Пометка для фотографий, где людей больше одного, но известен пока
 * только один — или ни одного.
 *
 * Считать групповым всё, к чему привязано двое и больше, недостаточно:
 * общий снимок, на котором мы опознали только бабушку, попадал в
 * «Портреты» и терялся среди них. Метка говорит «здесь есть кого искать».
 *
 * Берём штатные метки Gramps, а не свой атрибут: они видны и в родном
 * интерфейсе, и переживают выгрузку, и позже к ним лягут тематические
 * альбомы — «Свадьба», «Армия».
 */
export const GROUP_TAG = 'Групповое фото'

interface TagRaw {
  handle: string
  name: string
}

/** Handle метки, если она уже заведена. null — ещё ни разу не ставили. */
export async function findGroupTag(): Promise<string | null> {
  const tags = await apiGet<TagRaw[]>('/tags/')
  return tags.find(t => t.name === GROUP_TAG)?.handle || null
}

/** Handle метки; заводим, если её ещё нет. */
export async function groupTagHandle(): Promise<string> {
  const tags = await apiGet<TagRaw[]>('/tags/')
  const found = tags.find(t => t.name === GROUP_TAG)
  if (found) return found.handle
  const res = await apiPost<{handle?: string; _class?: string}[]>('/tags/', {
    _class: 'Tag',
    name: GROUP_TAG,
    // приглушённый акцент: метка не должна кричать в родном интерфейсе
    color: '#8A5A2BFF',
    priority: 0,
  })
  const h = (res || []).find(i => i._class === 'Tag')?.handle || res?.[0]?.handle
  if (!h) throw new Error('Сервер не вернул идентификатор метки')
  return h
}

/** Поставить или снять метку «групповое» у снимка. */
export async function setGroup(media: string, on: boolean): Promise<void> {
  const tag = await groupTagHandle()
  const m = await apiGet<Record<string, unknown>>(`/media/${media}`)
  const list = (m.tag_list as string[] | undefined) || []
  const has = list.includes(tag)
  if (has === on) return
  await apiPut(`/media/${media}`, {
    ...m,
    tag_list: on ? [...list, tag] : list.filter(t => t !== tag),
  })
}
